import "../inward-styles.css";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiMagnify,
  mdiPackageVariantClosed,
  mdiCurrencyInr,
  mdiAlertOutline,
  mdiClockOutline,
  mdiCloseCircleOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useCallback, useEffect, useState } from "react";
import LoadingWidget from "../../../../context_providers/modal/loader";
import { fetchAllQuarantinedList } from "../warehouse_module_repo";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import {
  DisputedItemModel,
  QuarantineFilters,
  QuarantineKPIs,
  QuarantinePagination,
} from "../model/quarantine_model";
import QuarantinedItemsList from "./QuarantinedItemsList";
import PaginationWarehouse from "../inward/PaginationWarehouse";
import { useSessionFilters } from "../contexts/useSessionFilters";
import { fetchGet } from "../../content_panel/services/serviceClient";
import { mdiEmailOutline } from "@mdi/js";
import "../inward-styles.css";

const QuarantineDashboard = () => {
  const { showToast } = useToast();

  const [showFilters, setShowFilters] = useState(window.innerWidth >= 768);

  const [filters, setFilters] = useSessionFilters<QuarantineFilters>(
    "warehouse:quarantine:filters",
    {
      search: "",
      status: "",
      type: "",
      fromDate: "",
      toDate: "",
      warehouse: "",
    },
  );
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<QuarantinePagination>({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 50,
    hasMore: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  const [quarantineList, setQuarantineList] = useState<DisputedItemModel[]>([]);
  const [statusCount, setStatusCount] = useState<QuarantineKPIs>({
    items: 0,
    value: 0,
    totalQty: 0,
    open: 0,
  });

  const refreshInwards = useCallback(async () => {
    setIsLoading(true);

    try {
      const payload: any = {
        ...filters,
        search: debouncedSearch?.trim(),
        warehouse: filters.warehouse?.trim(),
        page,
      };

      // Remove empty strings and null/undefined values
      Object.keys(payload).forEach((key) => {
        if (
          payload[key] === "" ||
          payload[key] === null ||
          payload[key] === undefined
        ) {
          delete payload[key];
        }
      });

      const res = await fetchAllQuarantinedList(payload);

      if (!res.success) {
        showToast({ type: ToastType.error, text: res.error });
        return;
      }

      console.log("qu list", res);
      setQuarantineList(res.data);

      if (res.pagination) {
        setPagination(res.pagination);
      }

      setStatusCount({
        items: res.kpis?.items ?? 0,
        value: res.kpis?.value ?? 0,
        totalQty: res.kpis?.totalQty ?? 0,
        open: res.kpis?.open ?? 0,
      });
    } catch (error: any) {
      showToast({
        type: ToastType.error,
        text: error.message || "Failed to load inwards",
      });
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, filters]);
  // Debounce search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [
    filters.status,
    filters.type,
    filters.fromDate,
    filters.toDate,
    filters.warehouse,
  ]);

  // Fetch data
  useEffect(() => {
    refreshInwards();
  }, [
    page,
    debouncedSearch,
    filters.status,
    filters.type,
    filters.fromDate,
    filters.toDate,
    filters.warehouse,
  ]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setShowFilters(!mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (filters.search) qs.append("search", filters.search);
      if (filters.status) qs.append("status", filters.status);
      if (filters.type) qs.append("type", filters.type);
      if (filters.warehouse) qs.append("warehouse", filters.warehouse.trim());
      if (filters.fromDate) qs.append("fromDate", filters.fromDate);
      if (filters.toDate) qs.append("toDate", filters.toDate);
      const res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/quarantine/export-quarantine-by-email?${qs.toString()}`,
      );
      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: res.message || "Export will be emailed shortly.",
        });
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to schedule export",
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || "Failed to schedule export",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      id="warehouse-quarantine-dashboard-root"
      className="wh-mobile-shrink flex flex-col gap-2 min-h-screen"
    >
      <div className="p-4 md:p-6 animate-fade-in">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <h1 className="text-3xl font-bold border-l-4 border-myPrimaryColor pl-2">
            Quarantine Management
          </h1>
          <button
            id="warehouse-quarantine-export-button"
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Icon path={mdiEmailOutline} size={0.8} />
            {exporting ? "Scheduling…" : "Export by Email"}
          </button>
        </div>

        <div className="space-y-6 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-2">
            {/* Items */}
            <div className="rounded-lg border bg-white text-card-foreground shadow-sm border-l-4 border-l-warning transition-all hover:shadow-md">
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-yellow-100 p-2 text-yellow-400">
                    <Icon path={mdiPackageVariantClosed} size={0.8} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Items</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {statusCount.items}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Value */}
            <div className="rounded-lg border bg-white text-card-foreground shadow-sm border-l-4 border-l-info transition-all hover:shadow-md">
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-blue-100 p-2 text-blue-400">
                    <Icon path={mdiCurrencyInr} size={0.8} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Value</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {new Intl.NumberFormat("en-IN", {
                        style: "currency",
                        currency: "INR",
                        maximumFractionDigits: 0,
                      }).format(statusCount.value)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Qty */}
            <div className="rounded-lg border bg-white text-card-foreground shadow-sm border-l-4 border-l-primary transition-all hover:shadow-md">
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-blue-200 p-2 text-blue-400">
                    <Icon path={mdiAlertOutline} size={0.8} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">
                      Total Qty
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {statusCount.totalQty}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pending */}
            <div className="rounded-lg border bg-white text-card-foreground shadow-sm border-l-4 border-l-destructive transition-all hover:shadow-md">
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-red-100 p-2 text-red-400">
                    <Icon path={mdiClockOutline} size={0.8} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Open</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {statusCount.open}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div
              id="warehouse-quarantine-filters-toggle"
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setShowFilters((p) => !p)}
            >
              <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                Filters
                <Icon
                  path={showFilters ? mdiChevronUp : mdiChevronDown}
                  size={0.8}
                />
              </div>
            </div>

            {(filters.search ||
              filters.status ||
              filters.type ||
              filters.fromDate ||
              filters.toDate ||
              filters.warehouse) && (
              <button
                id="warehouse-quarantine-clear-all-filters-button"
                onClick={() =>
                  setFilters({
                    search: "",
                    status: "",
                    type: "",
                    fromDate: "",
                    toDate: "",
                    warehouse: "",
                  })
                }
                className="text-sm font-semibold text-red-600 hover:text-red-700 flex items-center gap-1 transition-colors"
              >
                <Icon path={mdiCloseCircleOutline} size={0.6} />
                Clear All Filters
              </button>
            )}
          </div>

          {showFilters && (
            <div className="rounded-lg border border-gray-300 shadow-sm bg-white">
              <div className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Search */}
                  <div className="flex-1 flex items-center gap-2">
                    <div className="relative flex-1">
                      <Icon
                        path={mdiMagnify}
                        className="absolute left-3 top-1/4 -translate-y-1/2 h-5 w-5 text-muted-foreground"
                      />
                      <input
                        id="warehouse-quarantine-search-input"
                        className="flex h-10 w-full rounded-md border bg-background px-3 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Search by Order ID or Vendor..."
                        value={filters.search || ""}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            search: e.target.value,
                          }))
                        }
                      />
                    </div>
                    {filters.search && (
                      <button
                        id="warehouse-quarantine-clear-search-button"
                        onClick={() =>
                          setFilters((prev) => ({ ...prev, search: "" }))
                        }
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Clear Search"
                      >
                        <Icon path={mdiCloseCircleOutline} size={0.7} />
                      </button>
                    )}
                  </div>

                  {/* Status Dropdown */}
                  <div className="flex items-center gap-2">
                    <div className="relative w-full md:w-40">
                      <select
                        id="warehouse-quarantine-status-select"
                        value={filters.status || ""}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            status: e.target.value,
                          }))
                        }
                        className="
                           appearance-none
                           flex h-10 w-full items-center justify-between
                           rounded-md border bg-background
                           px-3 py-2 pr-8 text-sm
                           hover:bg-muted
                           focus:outline-none focus:ring-2 focus:ring-ring
                           cursor-pointer
                         "
                      >
                        <option value="">All Statuses</option>
                        <option value="open">Open</option>
                        <option value="returned">Returned</option>
                        <option value="resolved">Resolved</option>
                      </select>

                      <Icon
                        path={mdiChevronDown}
                        className="pointer-events-none absolute right-2 top-1/3 h-5 w-5 -translate-y-1/2 opacity-70"
                      />
                    </div>
                    {filters.status && (
                      <button
                        id="warehouse-quarantine-clear-status-button"
                        onClick={() =>
                          setFilters((prev) => ({ ...prev, status: "" }))
                        }
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Clear Status"
                      >
                        <Icon path={mdiCloseCircleOutline} size={0.7} />
                      </button>
                    )}
                  </div>

                  {/* Type Dropdown */}
                  <div className="flex items-center gap-2">
                    <div className="relative w-full md:w-40">
                      <select
                        id="warehouse-quarantine-type-select"
                        value={filters.type || ""}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            type: e.target.value,
                          }))
                        }
                        className="
                           appearance-none
                           flex h-10 w-full items-center justify-between
                           rounded-md border bg-background
                           px-3 py-2 pr-8 text-sm
                           hover:bg-muted
                           focus:outline-none focus:ring-2 focus:ring-ring
                           cursor-pointer
                         "
                      >
                        <option value="">All Dispute Types</option>
                        <option value="quantity_mismatch">
                          Quantity Mismatch
                        </option>
                        <option value="quality_issue">Quality Issue</option>
                        <option value="documentation_issue">
                          Documentation Issue
                        </option>
                        <option value="rate_mismatch">Rate Mismatch</option>
                      </select>

                      <Icon
                        path={mdiChevronDown}
                        className="pointer-events-none absolute right-2 top-1/3 h-5 w-5 -translate-y-1/2 opacity-70"
                      />
                    </div>
                    {filters.type && (
                      <button
                        id="warehouse-quarantine-clear-type-button"
                        onClick={() =>
                          setFilters((prev) => ({ ...prev, type: "" }))
                        }
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Clear Type"
                      >
                        <Icon path={mdiCloseCircleOutline} size={0.7} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* DATE & WAREHOUSE FILTERS */}
              <div className="p-4 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      id="warehouse-quarantine-from-date-input"
                      type="date"
                      value={filters.fromDate || ""}
                      onChange={(e) =>
                        setFilters((p) => ({ ...p, fromDate: e.target.value }))
                      }
                      className="h-10 w-full rounded-md border px-3 text-sm"
                      placeholder="From Date"
                    />
                    {filters.fromDate && (
                      <button
                        id="warehouse-quarantine-clear-from-date-button"
                        onClick={() =>
                          setFilters((p) => ({ ...p, fromDate: "" }))
                        }
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Icon path={mdiCloseCircleOutline} size={0.7} />
                      </button>
                    )}
                  </div>

                  {/* To Date */}
                  <div className="flex items-center gap-2">
                    <input
                      id="warehouse-quarantine-to-date-input"
                      type="date"
                      value={filters.toDate || ""}
                      onChange={(e) =>
                        setFilters((p) => ({ ...p, toDate: e.target.value }))
                      }
                      className="h-10 w-full rounded-md border px-3 text-sm"
                      placeholder="To Date"
                    />
                    {filters.toDate && (
                      <button
                        id="warehouse-quarantine-clear-to-date-button"
                        onClick={() =>
                          setFilters((p) => ({ ...p, toDate: "" }))
                        }
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Icon path={mdiCloseCircleOutline} size={0.7} />
                      </button>
                    )}
                  </div>

                  {/* Warehouse */}
                  <div className="flex items-center gap-2">
                    <input
                      id="warehouse-quarantine-warehouse-input"
                      placeholder="Warehouse (Name / ID)"
                      value={filters.warehouse || ""}
                      onChange={(e) =>
                        setFilters((p) => ({
                          ...p,
                          warehouse: e.target.value,
                        }))
                      }
                      className="h-10 w-full rounded-md border px-3 text-sm"
                    />
                    {filters.warehouse && (
                      <button
                        id="warehouse-quarantine-clear-warehouse-button"
                        onClick={() =>
                          setFilters((p) => ({ ...p, warehouse: "" }))
                        }
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Icon path={mdiCloseCircleOutline} size={0.7} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {quarantineList && quarantineList.length > 0 && (
            <>
              <QuarantinedItemsList
                items={quarantineList}
                onRefresh={refreshInwards}
              />

              <PaginationWarehouse
                page={page}
                totalPages={pagination.totalPages}
                onPageChange={(newPage) => {
                  if (newPage >= 1 && newPage <= pagination.totalPages) {
                    setPage(newPage);
                  }
                }}
              />
            </>
          )}

          {quarantineList && quarantineList.length === 0 && !isLoading && (
            <p className="text-center text-muted-foreground mt-10">
              No quarantined items found.
            </p>
          )}
        </div>
      </div>

      {isLoading && <LoadingWidget />}
    </div>
  );
};

export default QuarantineDashboard;
